import AttachFileIcon from "@mui/icons-material/AttachFile";
import CheckCircleOutlinedIcon from "@mui/icons-material/CheckCircleOutlined";
import CircleOutlinedIcon from "@mui/icons-material/CircleOutlined";
import CloseIcon from "@mui/icons-material/Close";
import CloudUploadIcon from "@mui/icons-material/CloudUpload";
import DeleteIcon from "@mui/icons-material/Delete";
import DownloadIcon from "@mui/icons-material/Download";
import InsertDriveFileIcon from "@mui/icons-material/InsertDriveFile";
import VisibilityIcon from "@mui/icons-material/Visibility";
import {
	Box,
	Button,
	Dialog,
	DialogContent,
	DialogTitle,
	Divider,
	FormControl,
	IconButton,
	InputLabel,
	MenuItem,
	Paper,
	Select,
	Stack,
	Tooltip,
	Typography,
	useMediaQuery,
	useTheme,
} from "@mui/material";
import { format } from "date-fns";
import { useEffect, useState } from "react";
import { LoadingComp } from "../../../../shared/components/loading/Loading";
import { useToast } from "../../../../shared/hooks";
import { SinisterDocumentsService } from "../../../../shared/services/api/sinister/SinisterDocumentsService";
import { ISinistercheckList, SinisterService } from "../../../../shared/services/api/sinister/SinisterService";
import { returnMessageErrorAsync } from "../../../../shared/utils/returnMessageError";
import { ModalAttachment, normalizeFileName } from "./ModalAttachment";
import { ModalPreviewValue } from "./ModalPreviewValue";

const SUPPORTED_FORMATS = ["png", "jpg", "jpeg", "doc", "docx", "pdf", "odt"];

type TPendingBulkFile = {
	id: string;
	file: File;
	checklistItemId: string;
};

interface Props {
	isOpen: boolean;
	onClose: () => void;
	id: string;
	busyProperty: string;
	showButtonAttachment: boolean;
}

const isItemRequired = (item: ISinistercheckList, context: "open" | "busy"): boolean => {
	return context === "open" ? item.open === "required" : item.busy === "required";
};

export const ModalDocumentsSinister: React.FC<Props> = ({ isOpen, onClose, id, busyProperty, showButtonAttachment }) => {
	const theme = useTheme();
	const fullScreen = useMediaQuery(theme.breakpoints.down("md"));
	const [previewDoc, setPreviewDoc] = useState<any>(null);
	const [previewUrl, setPreviewUrl] = useState<string | null>(null);
	const [isLoading, setIsLoading] = useState(false);
	const [checklist, setChecklist] = useState<ISinistercheckList[]>();
	const toastHandler = useToast();
	const [openAttachmentModal, setOpenAttachmentModal] = useState(false);
	const [currentItemId, setCurrentItemId] = useState<string | null>(null);
	const [item, setItem] = useState<any>("");
	// Envio em lote (mescla pedida: manter o checklist de obrigatório/anexado, mas permitir
	// selecionar vários arquivos de uma vez e, pra cada um, escolher a que item do checklist ele
	// corresponde — em vez de precisar clicar "Anexar" item por item).
	const [pendingBulkFiles, setPendingBulkFiles] = useState<TPendingBulkFile[]>([]);
	const [isBulkUploading, setIsBulkUploading] = useState(false);

	const handleCheckList = async (id: string, ocuped: string) => {
		try {
			setIsLoading(true);
			const result = await SinisterService.getSinisterCheckList(id, ocuped);

			if (!result) {
				toastHandler.present({
					type: "error",
					messageOrContent: "Erro ao carregar a checklist de documentos.",
					position: "top-center",
				});
				return;
			}

			const listaDocuments = await Promise.all(
				result.data.map(async (item) => {
					const documentList = await SinisterService.getSinisterDocumentByList(id, item.Id);
					return {
						...item,
						sinisterDocuments: documentList,
					};
				})
			);

			setChecklist(listaDocuments);
		} catch (error) {
			toastHandler.present({
				type: "error",
				messageOrContent: "Erro ao carregar os documentos.",
				position: "top-center",
			});
		} finally {
			setIsLoading(false);
		}
	};

	const handleOpenPreview = async (doc: any) => {
		try {
			setIsLoading(true);
			if (previewUrl) {
				URL.revokeObjectURL(previewUrl);
				setPreviewUrl(null);
			}

			const result = await SinisterDocumentsService.previewDocumentBySinister(doc.id);
			if (result instanceof Error) throw result;
			const blobUrl = URL.createObjectURL(result);
			setPreviewUrl(blobUrl);
			setPreviewDoc(doc);
		} catch (error) {
			toastHandler.present({
				type: "error",
				messageOrContent: "Erro ao visualizar o documento!",
				position: "top-center",
			});
			setPreviewDoc(null);
		} finally {
			setIsLoading(false);
		}
	};

	const handleClosePreview = () => {
		setPreviewDoc(null);
		if (previewUrl) {
			URL.revokeObjectURL(previewUrl);
			setPreviewUrl(null);
		}
	};

	const handleDownloadAll = async () => {
		try {
			await SinisterService.getDownloadAll(id);
		} catch (error) {
			toastHandler.present({
				type: "error",
				messageOrContent: "Erro ao baixar os documentos.",
				position: "top-center",
			});
		}
	};

	const handleAttachClick = (itemId: string) => {
		setCurrentItemId(itemId);
		setOpenAttachmentModal(true);
		setItem(checklist?.find((i) => i.Id === itemId));
	};

	const handleAttachFile = async (file: File) => {
		if (!file || !currentItemId) return;
		try {
			await SinisterDocumentsService.uploadSinisterDocument(id, currentItemId, "otherAtachments", file);
			toastHandler.present({
				type: "success",
				messageOrContent: `Arquivo '${file.name}' enviado com sucesso!`,
				position: "top-center",
			});
			handleCheckList(id, busyProperty); // Atualiza lista após upload
		} catch (error: any) {
			const { titleMessage, contentMessage } = await returnMessageErrorAsync(error);
			toastHandler.present({
				type: "error",
				messageOrContent: `${titleMessage} ${contentMessage}`,
				position: "top-center",
			});
		} finally {
			setOpenAttachmentModal(false);
		}
	};

	const handleRemoveFile = () => {
		setOpenAttachmentModal(false);
	};

	const handleBulkFileSelect = (event: React.ChangeEvent<HTMLInputElement>) => {
		const files = event.target.files;
		if (!files || files.length === 0) return;

		const accepted: TPendingBulkFile[] = [];
		const rejected: string[] = [];

		Array.from(files).forEach((file) => {
			const extension = file.name.split(".").pop()?.toLowerCase();
			if (!extension || !SUPPORTED_FORMATS.includes(extension)) {
				rejected.push(file.name);
				return;
			}
			const normalizedFile = new File([file], normalizeFileName(file.name), { type: file.type });
			accepted.push({ id: `${Date.now()}-${Math.random()}`, file: normalizedFile, checklistItemId: "" });
		});

		if (rejected.length > 0) {
			toastHandler.present({
				type: "error",
				messageOrContent: `Formato inválido: ${rejected.join(", ")}. Permitidos: ${SUPPORTED_FORMATS.join(", ").toUpperCase()}`,
				position: "top-center",
			});
		}

		setPendingBulkFiles((prev) => [...prev, ...accepted]);
		event.target.value = "";
	};

	const handleBulkTypeChange = (fileId: string, checklistItemId: string) => {
		setPendingBulkFiles((prev) => prev.map((item) => (item.id === fileId ? { ...item, checklistItemId } : item)));
	};

	const handleBulkRemove = (fileId: string) => {
		setPendingBulkFiles((prev) => prev.filter((item) => item.id !== fileId));
	};

	const handleBulkSubmit = async () => {
		if (pendingBulkFiles.length === 0) return;

		if (pendingBulkFiles.some((item) => !item.checklistItemId)) {
			toastHandler.present({
				type: "error",
				messageOrContent: "Selecione o tipo de documento de todos os arquivos antes de enviar.",
				position: "top-center",
			});
			return;
		}

		setIsBulkUploading(true);
		const results = await Promise.allSettled(
			pendingBulkFiles.map((item) => SinisterDocumentsService.uploadSinisterDocument(id, item.checklistItemId, "otherAtachments", item.file))
		);
		setIsBulkUploading(false);

		const successCount = results.filter((r) => r.status === "fulfilled").length;
		const failureCount = results.length - successCount;

		if (successCount > 0) {
			toastHandler.present({
				type: failureCount > 0 ? "warning" : "success",
				messageOrContent: `${successCount} arquivo(s) enviado(s) com sucesso${failureCount > 0 ? ` — ${failureCount} falharam` : ""}.`,
				position: "top-center",
			});
		}
		if (successCount === 0) {
			toastHandler.present({
				type: "error",
				messageOrContent: "Não foi possível enviar os arquivos.",
				position: "top-center",
			});
		}

		setPendingBulkFiles((prev) => prev.filter((_, i) => results[i].status !== "fulfilled"));
		handleCheckList(id, busyProperty);
	};

	useEffect(() => {
		if (isOpen) {
			handleCheckList(id, busyProperty);
		}
	}, [isOpen]);

	return (
		<>
			{isLoading && <LoadingComp isopen />}

			<Dialog open={isOpen} onClose={onClose} fullScreen={fullScreen} maxWidth="md" fullWidth>
				<DialogTitle sx={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
					<Typography variant="h5" fontWeight={300} color="primary.main">
						Documentos do Sinistro
					</Typography>
					<IconButton aria-label="fechar" onClick={onClose} size="small">
						<CloseIcon />
					</IconButton>
				</DialogTitle>

				<DialogContent dividers>
					{/* Cabeçalho e botão baixar */}
					<Paper elevation={0} sx={{ backgroundColor: "#f9f9f9", border: "1px solid #e0e0e0", p: 2, mb: 3, borderRadius: 2 }}>
						<Stack
							direction={{ xs: "column", sm: "row" }}
							spacing={2}
							justifyContent="space-between"
							alignItems={{ xs: "flex-start", sm: "center" }}
						>
							<Box flex={1}>
								<Typography variant="body2" gutterBottom>
									Para celeridade e regulação do seu sinistro, favor anexar os documentos solicitados abaixo...
								</Typography>
								<Typography variant="caption" color="text.secondary">
									São aceitos documentos de até 50MB.
								</Typography>
							</Box>
							<Tooltip title="Baixar todos os anexos">
								<Button variant="outlined" size="small" startIcon={<DownloadIcon />} onClick={handleDownloadAll}>
									Baixar Anexos
								</Button>
							</Tooltip>
						</Stack>
					</Paper>

					{/* Envio em lote — seleciona vários arquivos de uma vez e define o tipo (item do
					checklist) de cada um antes de enviar, em vez de anexar item por item. */}
					{showButtonAttachment && (
						<Paper variant="outlined" sx={{ p: 2, mb: 3, borderRadius: 2 }}>
							<Stack direction="row" justifyContent="space-between" alignItems="center" mb={pendingBulkFiles.length > 0 ? 2 : 0}>
								<Typography variant="body2" fontWeight={600}>
									Envio em lote
								</Typography>
								<Button
									component="label"
									size="small"
									variant="outlined"
									startIcon={<CloudUploadIcon />}
									disabled={isBulkUploading}
								>
									Selecionar arquivos
									<input type="file" multiple hidden onChange={handleBulkFileSelect} />
								</Button>
							</Stack>

							{pendingBulkFiles.length > 0 && (
								<Stack spacing={1.5}>
									{pendingBulkFiles.map((pending) => (
										<Stack key={pending.id} direction={{ xs: "column", sm: "row" }} spacing={1} alignItems={{ sm: "center" }}>
											<Stack direction="row" spacing={1} alignItems="center" flex={1} minWidth={0}>
												<InsertDriveFileIcon fontSize="small" sx={{ color: "#999" }} />
												<Typography variant="body2" noWrap>
													{pending.file.name}
												</Typography>
											</Stack>
											<FormControl size="small" sx={{ minWidth: 220 }}>
												<InputLabel id={`bulk-type-${pending.id}`}>Tipo de documento</InputLabel>
												<Select
													labelId={`bulk-type-${pending.id}`}
													label="Tipo de documento"
													value={pending.checklistItemId}
													onChange={(e) => handleBulkTypeChange(pending.id, e.target.value as string)}
												>
													{(checklist ?? []).map((checklistItem) => (
														<MenuItem key={checklistItem.Id} value={checklistItem.Id}>
															{checklistItem.description}
														</MenuItem>
													))}
												</Select>
											</FormControl>
											<IconButton size="small" onClick={() => handleBulkRemove(pending.id)} disabled={isBulkUploading}>
												<DeleteIcon fontSize="small" />
											</IconButton>
										</Stack>
									))}

									<Box display="flex" justifyContent="flex-end">
										<Button variant="contained" size="small" disabled={isBulkUploading} onClick={handleBulkSubmit}>
											Enviar {pendingBulkFiles.length} arquivo(s)
										</Button>
									</Box>
								</Stack>
							)}
						</Paper>
					)}

					{/* Lista de documentos */}
					{checklist?.length ? (
						<Stack spacing={2}>
							{[...(checklist ?? [])]
								.sort((a, b) => {
									const lastA = [...(a.sinisterDocuments ?? [])].sort(
										(a, b) => new Date(b.uploadedDate).getTime() - new Date(a.uploadedDate).getTime()
									)[0];
									const lastB = [...(b.sinisterDocuments ?? [])].sort(
										(a, b) => new Date(b.uploadedDate).getTime() - new Date(a.uploadedDate).getTime()
									)[0];

									const dateA = lastA?.uploadedDate ? new Date(lastA.uploadedDate).getTime() : 0;
									const dateB = lastB?.uploadedDate ? new Date(lastB.uploadedDate).getTime() : 0;
									return dateB - dateA;
								})
								.map((item) => {
									// ... (todo seu render de cada item vai aqui)

									const latestDoc = [...(item.sinisterDocuments || [])].sort(
										(a, b) => new Date(b.uploadedDate || "").getTime() - new Date(a.uploadedDate || "").getTime()
									)[0];
									const required = isItemRequired(item, busyProperty as "open" | "busy");

									return (
										<Paper
											key={item.Id}
											variant="outlined"
											sx={{
												px: 2,
												py: 1,
												backgroundColor:
													item.quant !== "0"
														? "#f6fffa"
														: required && (item.sinisterDocuments?.length ?? 0) === 0
														? "#fff0f0"
														: "#fff",
												borderLeft: `4px solid ${item.quant !== "0" ? "#4caf50" : required ? "#f44336" : "primary.main"}`,
											}}
										>
											<Stack direction="row" justifyContent="space-between" spacing={2}>
												<Stack direction="row" spacing={1} flex={1}>
													{item.quant !== "0" ? (
														<CheckCircleOutlinedIcon sx={{ color: "#4caf50" }} />
													) : (
														<CircleOutlinedIcon sx={{ color: "primary.main" }} />
													)}
													<Box>
														<Typography variant="body2" fontWeight="bold">
															{item.description}
															{required && (
																<Typography
																	component="span"
																	sx={{
																		color: item.sinisterDocuments?.length ? "success.main" : "error.main",
																		fontWeight: 500,
																	}}
																>
																	{item.sinisterDocuments?.length ? " — Anexado" : " — Obrigatório"}
																</Typography>
															)}
														</Typography>
														<Typography variant="caption" color="text.secondary">
															{item.sinisterDocuments?.length
																? `Anexado(s): ${item.sinisterDocuments.length} — Último envio: ${format(
																		new Date(latestDoc?.uploadedDate || ""),
																		"dd/MM/yyyy HH:mm"
																  )}`
																: "Nenhum documento enviado"}
														</Typography>
													</Box>
												</Stack>
												{showButtonAttachment && (
													<Tooltip title="Anexar documento">
														<Button
															variant="outlined"
															size="small"
															onClick={() => handleAttachClick(item.Id)}
															startIcon={<AttachFileIcon />}
															sx={{ whiteSpace: "nowrap" }}
														>
															Anexar
														</Button>
													</Tooltip>
												)}
											</Stack>
											{(item.sinisterDocuments ?? []).length > 0 && (
												<Stack mt={0.5} spacing={0.5} sx={{ pl: 3 }}>
													{[...(item.sinisterDocuments ?? [])]
														.sort((a, b) => new Date(b.uploadedDate).getTime() - new Date(a.uploadedDate).getTime())
														.map((doc, i) => (
															<Box key={i}>
																<Stack direction="row" justifyContent="space-between" sx={{ minHeight: 36 }}>
																	<Stack direction="row" spacing={1} flex={1}>
																		<InsertDriveFileIcon fontSize="small" sx={{ color: "#999" }} />
																		<Stack spacing={0.5}>
																			<Typography variant="body2" fontWeight={500} lineHeight={1.2}>
																				{doc.originalFilename}
																			</Typography>
																			<Typography variant="caption" color="text.secondary" lineHeight={1}>
																				{format(new Date(doc.uploadedDate), "dd/MM/yyyy HH:mm")}
																			</Typography>
																		</Stack>
																	</Stack>
																	<Stack direction="row" spacing={0.5}>
																		<Tooltip title="Visualizar">
																			<IconButton size="small" onClick={() => handleOpenPreview(doc)}>
																				<VisibilityIcon fontSize="small" />
																			</IconButton>
																		</Tooltip>
																		<Tooltip title="Baixar">
																			<IconButton
																				size="small"
																				component="a"
																				href={doc.url}
																				target="_blank"
																				rel="noopener noreferrer"
																			>
																				<DownloadIcon fontSize="small" />
																			</IconButton>
																		</Tooltip>
																	</Stack>
																</Stack>
																<Divider sx={{ mt: 0.5 }} />
															</Box>
														))}
												</Stack>
											)}
										</Paper>
									);
								})}
						</Stack>
					) : (
						<Typography variant="body2">Nenhum documento encontrado.</Typography>
					)}
				</DialogContent>
			</Dialog>

			<ModalPreviewValue open={!!previewDoc} onClose={handleClosePreview} previewDoc={previewDoc} previewUrl={previewUrl} isLoading={false} />
			<ModalAttachment
				isOpen={openAttachmentModal}
				onClose={() => setOpenAttachmentModal(false)}
				onAttach={handleAttachFile}
				onRemove={handleRemoveFile}
				nameDocument={item.description || ""}
			/>
		</>
	);
};
